import clsx from 'clsx';
import HubertLabel from 'components/HubertUI/HubertLabel';
import {
  ECreateQuestionMode,
  useQuestionEditorContext,
} from 'components/Questionsv2/builder/context/create-question.context';
import SliderEvaluation from 'components/Templates/builder/Evaluation/EvaluationPopover/SliderEvaluation';
import { useQuestionEvaluation } from 'hooks/useQuestionEvaluation';
import { useTemplateId } from 'hooks/useTemplate';
import type { ComponentProps } from 'react';
import { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { updateFormField } from 'rtk/Question.Editor.Slice';
import { selectQuestionMode } from 'rtk/questionEditorSelector';
import { setCustomEvaluation } from 'rtk/TemplateSlice';
import { EEventType } from 'types/events.enum';
import { EQuestionType } from '@hubertai/screening-question-types';
import type { TFormFieldUpdater } from '../FormFieldUpdater';
import FormFieldUpdater from '../FormFieldUpdater';
import { type TFormSlider } from '@hubertai/screening-question-types';
import { useTranslation } from 'react-i18next';

interface Props extends ComponentProps<'div'> {
  form: TFormSlider;
  formIndex: number;
}

const CreateFormSlider = ({ form, formIndex, ...props }: Props) => {
  const { t } = useTranslation('Questionsv2');
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const templateId = useTemplateId();
  const { evaluation, isLoading: isEvaluationLoading } = useQuestionEvaluation(questionId);

  const formFieldValues: TFormFieldUpdater[] = useMemo(
    () => [
      {
        label: t('form.slider.labelLeft.label'),
        description: t('form.slider.labelLeft.description'),
        formKey: 'label_left',
        fieldType: 'text',
        formValuesKey: 'labelLeft',
        form,
        formIndex,
      },
      {
        label: t('form.slider.labelRight.label'),
        description: t('form.slider.labelRight.description'),
        formKey: 'label_right',
        fieldType: 'text',
        formValuesKey: 'labelRight',
        form,
        formIndex,
      },
      {
        label: t('form.slider.unit.label'),
        description: t('form.slider.unit.description'),
        formKey: 'unit',
        fieldType: 'text',
        formValuesKey: 'unit',
        form,
        formIndex,
      },
      {
        label: t('form.slider.min.label'),
        description: t('form.slider.min.description'),
        formKey: 'min',
        fieldType: 'number',
        formValuesKey: 'min',
        form,
        formIndex,
      },
      {
        label: t('form.slider.max.label'),
        description: t('form.slider.max.description'),
        formKey: 'max',
        fieldType: 'number',
        formValuesKey: 'max',
        form,
        formIndex,
      },
      {
        label: t('form.slider.step.label'),
        description: t('form.slider.step.description'),
        formKey: 'step',
        fieldType: 'number',
        formValuesKey: 'step',
        form,
        formIndex,
      },
    ],
    [t, form, formIndex],
  );

  return (
    <div {...props}>
      {formFieldValues.map((x) => {
        return (
          <FormFieldUpdater
            key={`form-field-updater-${form.id}`}
            label={x.label}
            description={x.description}
            formKey={x.formKey}
            fieldType={x.fieldType}
            formValuesKey={x.formValuesKey}
            form={x.form}
            formIndex={x.formIndex}
          />
        );
      })}

      {isEvaluationLoading ? null : (
        <div {...props} className={clsx('w-full', 'flex-row- flex justify-between', 'hubert-vertical-center', 'my-4')}>
          <div>
            <HubertLabel>{t('form.slider.evaluation')}</HubertLabel>
          </div>

          <div>
            <SliderEvaluation
              form={form}
              // @ts-ignore
              evaluation={evaluation?.evaluation[form.id]}
              onEvaluationChange={(e: any) => {
                dispatch(updateFormField({ questionId, formIndex, field: 'evaluation', value: e }));

                if (templateId && mode === ECreateQuestionMode.InTemplateEdit) {
                  dispatch(
                    setCustomEvaluation({
                      templateId,
                      eventType: EEventType.MODIFIED,
                      resource: {
                        nodeId: questionId,
                        nodeType: EQuestionType.Form,
                        formType: form.type,
                        key: form.id,
                        evaluation: e,
                      },
                      description: `User updated evaluation to ${e} for form ${form.id} in question ${questionId} in template ${templateId}.`,
                    }),
                  );
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateFormSlider;
